<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeMount, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { formatParsedClockTime, parseClockTimeInput } from '@nova/core';

interface TimeEntryValue {
  amPm: string;
  time: string;
}

type ValidationRule = (value: string) => boolean | string;

const {
  modelValue = { amPm: '', time: '' },
  disabled = false,
  timezone,
  militaryTimeEnabled,
  required = false,
  compact = false,
  hideTimezone = false
} = defineProps<{
  modelValue?: TimeEntryValue;
  disabled?: boolean;
  timezone: string;
  militaryTimeEnabled: boolean;
  required?: boolean;
  compact?: boolean;
  hideTimezone?: boolean;
}>();

const emit = defineEmits<{
  'update:model-value': [value: TimeEntryValue];
}>();

const time = ref('');
const amPm = ref('');

const instance = getCurrentInstance();

const timeRules = computed<ValidationRule[]>(() => {
  if (!required && time.value.length === 0) {
    return [];
  }
  /**
   * Apps without the $validator global (e.g. Circe, Juno) get no time-format message
   * instead of a crash — this branch is otherwise identical for apps that do install it.
   */
  const validatorRules = instance?.appContext.config.globalProperties.$validator?.rules;
  return (militaryTimeEnabled ? validatorRules?.time24 : validatorRules?.time) ?? [];
});

function onTimeInput(value: string) {
  const digitsAndColon = String(value ?? '').replace(/[^\d:]/gu, '');
  const colonIndex = digitsAndColon.indexOf(':');

  if (colonIndex === -1) {
    time.value = digitsAndColon.slice(0, 4);
    return;
  }

  const hour = digitsAndColon.slice(0, colonIndex).replace(/:/gu, '').slice(0, 2);
  const minute = digitsAndColon
    .slice(colonIndex + 1)
    .replace(/:/gu, '')
    .slice(0, 2);
  time.value = `${hour}:${minute}`;
}

function normalizeTime() {
  const parsed = parseClockTimeInput(time.value, militaryTimeEnabled);
  if (parsed) {
    time.value = formatParsedClockTime(parsed);
  }
}

function updateVal() {
  if (time.value.length === 5 && militaryTimeEnabled) {
    const parsed = DateTime.fromFormat(time.value, 'HH:mm');
    /**
     * Luxon stringifies an unparseable DateTime as the literal "Invalid DateTime", so an
     * unguarded toFormat here writes that text into amPm and emits it to the parent as if it
     * were a real value.
     */
    if (parsed.isValid) {
      amPm.value = parsed.toFormat('a');
    }
  }

  emit('update:model-value', {
    amPm: amPm.value,
    time: time.value
  });
}

/**
 * Only a 12-hour value carries an amPm, and only that needs converting to the 24-hour display.
 * In military mode the parent already hands over 24-hour time with an empty amPm: parsing that
 * as 'hh:mm a' produced an invalid DateTime whose "Invalid DateTime" string was then written
 * straight back to the parent by updateVal, discarding a valid stored timestamp without the
 * user touching anything.
 */
function updateLocalTime() {
  if (militaryTimeEnabled && modelValue.time.length === 5 && modelValue.amPm) {
    const converted = DateTime.fromFormat(`${modelValue.time} ${modelValue.amPm}`, 'hh:mm a');
    if (converted.isValid) {
      time.value = converted.toFormat('HH:mm');
      amPm.value = converted.toFormat('a');
      return;
    }
  }

  time.value = modelValue.time;
  amPm.value = modelValue.amPm;
}

onBeforeMount(updateLocalTime);

watch(() => modelValue, updateLocalTime, { deep: true });
watch(amPm, updateVal);
watch(time, updateVal);
</script>

<template>
  <div class="d-flex flex-column flex-md-row flex-md-wrap ga-2 ga-md-4 time-entry">
    <div class="d-flex flex-column gap-1">
      <v-text-field
        v-bind="$props"
        color="primary"
        density="comfortable"
        hide-details="auto"
        inputmode="numeric"
        validate-on="blur"
        variant="outlined"
        :class="{ 'no-timezone': hideTimezone }"
        :data-testid="`${$attrs['test-id']}-input`"
        :model-value="time"
        :placeholder="militaryTimeEnabled ? '13:00' : '12:00'"
        :rules="timeRules"
        @blur="normalizeTime"
        @update:model-value="onTimeInput"></v-text-field>
      <small class="typography-caption" v-if="!hideTimezone">
        {{ timezone }}
      </small>
    </div>

    <v-btn-toggle
      v-if="!militaryTimeEnabled"
      divided
      mandatory
      class="flex-grow-1"
      color="primary"
      variant="outlined"
      :disabled="disabled"
      v-model="amPm">
      <v-btn
        value="am"
        :class="{ 'auto-width': compact }"
        :data-testid="`${$attrs['test-id']}-am-button`">
        AM
      </v-btn>
      <v-btn
        value="pm"
        :class="{ 'auto-width': compact }"
        :data-testid="`${$attrs['test-id']}-pm-button`">
        PM
      </v-btn>
    </v-btn-toggle>
  </div>
</template>

<style scoped lang="scss">
.no-timezone {
  width: 75px;
}
</style>
